import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import { api } from "../../services/api";

const ExamQuarterlyDistributionChart = () => {
  const [examData, setExamData] = useState({
    quarterCounts: {},
    totalExams: 0,
    loading: true,
    error: false,
  });

  // Chart series and options
  const [chartSeries, setChartSeries] = useState([]);
  const [chartLabels, setChartLabels] = useState([]);

  useEffect(() => {
    const fetchExamDistribution = async () => {
      try {
        // Fetch all exams from API
        const response = await api.exams.getAll();

        if (response.success && response.data) {
          const exams = response.data;

          // Calculate quarter counts based on createdAt dates
          const quarterCounts = {
            Q1: 0,
            Q2: 0,
            Q3: 0,
            Q4: 0,
          };

          // Process each exam to determine its quarter
          exams.forEach((exam) => {
            if (exam.createdAt) {
              const date = new Date(exam.createdAt);
              const month = date.getMonth(); // 0-11

              // Determine quarter
              if (month >= 0 && month <= 2) {
                // Jan-Mar (Q1)
                quarterCounts.Q1++;
              } else if (month >= 3 && month <= 5) {
                // Apr-Jun (Q2)
                quarterCounts.Q2++;
              } else if (month >= 6 && month <= 8) {
                // Jul-Sep (Q3)
                quarterCounts.Q3++;
              } else {
                // Oct-Dec (Q4)
                quarterCounts.Q4++;
              }
            }
          });

          // Calculate total exams
          const totalExams = Object.values(quarterCounts).reduce(
            (sum, count) => sum + count,
            0
          );

          // Prepare chart series data
          const series = [];
          const labels = [];

          // Define quarter labels
          const quarterLabels = {
            Q1: "Q1 (Jan-Mar)",
            Q2: "Q2 (Apr-Jun)",
            Q3: "Q3 (Jul-Sep)",
            Q4: "Q4 (Oct-Dec)",
          };

          // Ensure all quarters are represented
          Object.entries(quarterLabels).forEach(([quarter, label]) => {
            const count = quarterCounts[quarter] || 0;
            series.push(count);
            labels.push(label);
          });

          // Update chart data
          setChartSeries(series);
          setChartLabels(labels);

          // Update state
          setExamData({
            quarterCounts,
            totalExams,
            loading: false,
            error: false,
          });
        } else {
          throw new Error(
            response.message || "Failed to fetch exam distribution data"
          );
        }
      } catch (error) {
        console.error("Error fetching exam distribution data:", error);

        // Set fallback data
        const fallbackData = {
          Q1: 15,
          Q2: 22,
          Q3: 18,
          Q4: 29,
        };

        setChartSeries(Object.values(fallbackData));
        setChartLabels([
          "Q1 (Jan-Mar)",
          "Q2 (Apr-Jun)",
          "Q3 (Jul-Sep)",
          "Q4 (Oct-Dec)",
        ]);

        setExamData({
          quarterCounts: fallbackData,
          totalExams: 84,
          loading: false,
          error: true,
        });
      }
    };

    fetchExamDistribution();
  }, []);

  // Chart options
  const chartOptions = {
    chart: {
      type: "bar",
      height: 320,
      toolbar: {
        show: false,
      },
    },
    colors: ["#487FFF", "#45B369", "#FF9F29", "#FF6A49"],
    plotOptions: {
      bar: {
        borderRadius: 4,
        columnWidth: "50%",
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: chartLabels,
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: {
      title: {
        text: "Number of Exams",
      },
    },
    tooltip: {
      y: {
        formatter: function (value) {
          return value + " exams";
        },
      },
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            height: 300,
          },
        },
      },
    ],
  };

  // Render quarter counts as a horizontal bar graph
  const renderQuarterBars = () => {
    if (
      !examData.quarterCounts ||
      Object.keys(examData.quarterCounts).length === 0
    ) {
      return null;
    }

    const quarterColors = {
      Q1: "#487FFF",
      Q2: "#45B369",
      Q3: "#FF9F29",
      Q4: "#FF6A49",
    };

    const quarterLabels = {
      Q1: "Q1 (Jan-Mar)",
      Q2: "Q2 (Apr-Jun)",
      Q3: "Q3 (Jul-Sep)",
      Q4: "Q4 (Oct-Dec)",
    };

    return (
      <div className="mt-3">
        {Object.entries(examData.quarterCounts).map(([quarter, count]) => (
          <div key={quarter} className="mb-3">
            <div className="d-flex justify-content-between mb-1">
              <div className="fw-medium">{quarterLabels[quarter]}</div>
              <div className="fw-semibold">{count} exams</div>
            </div>
            <div className="progress" style={{ height: "10px" }}>
              <div
                className="progress-bar"
                role="progressbar"
                style={{
                  width: `${(count / examData.totalExams) * 100}%`,
                  backgroundColor: quarterColors[quarter] || "#6c757d",
                }}
                aria-valuenow={count}
                aria-valuemin="0"
                aria-valuemax={examData.totalExams}
              ></div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="col-xxl-6 col-xl-6">
      <div className="card h-100 radius-8 border">
        <div className="card-body p-24">
          <h6 className="mb-16 fw-semibold text-lg">
            Exam Distribution by Quarter
          </h6>

          {examData.error && (
            <div className="text-sm text-danger-main mb-2">
              <Icon icon="mdi:alert-circle-outline" className="me-1" />
              Using demo data (API error)
            </div>
          )}

          {examData.loading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              <div className="row">
                <div className="col-lg-7">{renderQuarterBars()}</div>
                <div className="col-lg-5">
                  {chartSeries.length > 0 ? (
                    <ReactApexChart
                      options={chartOptions}
                      series={[{ name: "Exams", data: chartSeries }]}
                      type="bar"
                      height={240}
                    />
                  ) : (
                    <div className="text-center py-5 text-muted">
                      No exam data available
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExamQuarterlyDistributionChart;
